/**
 * [SALES-PIPELINE 2026-10-07, user] Sales Pipeline — each sales rep's forecast board for the month.
 *
 * Top: Month + Sales Rep (My Sales' rule: rep = self, team lead = their team, admin/senior = everyone).
 * Add line: Company URL, Company Email, Payment Type, Deal Owner, Amount, Deal Stage, then Save. All required.
 *   Deal Owner = the rep's own name (locked). Admins and seniors start from a blank box and pick any sales rep.
 * Board: Commit / Likely / Upside / Early Pipeline. A card's stage dropdown moves it; the card glides to the
 *   top of its new column ("Glide", picked by the user from a live preview:
 *   https://claude.ai/artifact/1ZQY5iFXQ2vSdULcF5mnH7).
 * Every month starts empty and a past month opens read-only. No remove (user).
 * [STAGE-PCT 2026-10-12, user] The column headers and the Deal Stage dropdown show each stage's % in brackets
 *   ("Commit (90%)"); the card's own stage dropdown, the messages and the totals stay without it.
 * [CLOSE-DATE 2026-10-12, user] "Tentative Close Date" after Deal Stage (the app's calendar, required), shown on the
 *   card under Deal Owner and changeable there on an open month.
 * A "Saved" / "Moved" message appears only after the server has stored the change.
 * [REP-WISE 2026-10-13, user] Toolbar right: Sales Rep, Month, then a "Pipeline View | Rep wise data" switch (it stays in
 *   place while switching), for admins and seniors only. ([PIPELINE-VIEW-LABEL 2026-10-22] was "Main Screen".)
 *   Rep wise data = one row per rep (every rep at once), the four sections' amounts, a Total, and Declined in its own
 *   column; a Total row at the end. The Sales Rep pick is hidden there.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useTransition } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, DefaultTheme } from 'styled-components';
import { lgCardSheen, lgButtonPrimary, lgButtonSecondary, lgSunken, lgOnWhite, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { usePresence } from '../hooks/usePresence'; // [FLUID-1 2026-10-12]
import { fluid } from '../styles/motion'; // [FLUID-1 2026-10-12]
import { fluidPop, fluidCascade, useRowsLand } from '../components/Fluid'; // [FLUID-1 2026-10-12] [REP-WISE 2026-10-13] useRowsLand
import { SlideViewport, SlidePane, slidePos } from '../components/ViewSlide'; // [REP-WISE 2026-10-13]
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [REP-WISE 2026-10-13]
import GlassSelect from '../components/GlassSelect';
import SingleDateCalendar from '../components/SingleDateCalendar'; // [CLOSE-DATE 2026-10-12]
import { LoadFailed, isForbidden } from '../components/LoadState';
import { SkeletonBox } from '../components/Skeleton';
import { toast, toastError, toastSuccess } from '../components/toastService';
import { alertDialog } from '../components/dialogService'; // [DUP-POPUP 2026-10-07]
import { useRealtime } from '../hooks/useRealtime';
import { useAuth } from '../contexts/AuthContext'; // [REP-WISE 2026-10-13] admin / senior flags before the board lands
import { useSaveQueue, queuedToast } from '../hooks/useSaveQueue';
import { pillTrack, pillSegment } from '../styles/buttons'; // [REP-WISE 2026-10-13] pill*
import {
  fetchSalesPipeline, addPipelineDeal, movePipelineDeal, PipelineBoard, PipelineDeal, PipelineStage,
  fetchPipelineWebsiteSuggestions, fetchPipelineEmailSuggestions, setPipelineDealDeclined, editPipelineDeal,
  fetchPipelineActivity, PipelineCardActivity,
} from '../services/api';
import { layoutBetween, pageRect, toPage } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width; glides in page px

// [STAGE-PCT 2026-10-12, user] "after commit add 90 % in brackets, after likely 70%, after upside 40 percent and early
// pipeline 15 %" — in the column headers and the Deal Stage dropdown (stageWithPct).
const STAGES: { key: PipelineStage; label: string; pct: number }[] = [
  { key: 'commit', label: 'Commit', pct: 90 },
  { key: 'likely', label: 'Likely', pct: 70 },
  { key: 'upside', label: 'Upside', pct: 40 },
  { key: 'early', label: 'Early Pipeline', pct: 15 },
];
const stageWithPct = (s: { label: string; pct: number }) => `${s.label} (${s.pct}%)`;
const STAGE_LABEL: Record<PipelineStage, string> = {
  commit: 'Commit', likely: 'Likely', upside: 'Upside', early: 'Early Pipeline',
};
const PAYMENT_TYPES = ['Recurring', 'One-Time'];

// [CLOSE-DATE 2026-10-12] Today on this computer (the calendar's own "today"), and a saved day the way the
// calendar writes it ("Oct 25, 2026").
const pad2 = (n: number) => String(n).padStart(2, '0');
const todayKey = () => {
  const n = new Date();
  return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}`;
};
const CLOSE_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const closeDateText = (ymd: string | null | undefined) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  return m ? CLOSE_FMT.format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))) : 'not set';
};

// Same account as the Payments tab's deal links; 0-2 is HubSpot's Companies object.
const HUBSPOT_PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID || '3835383';
const hubspotCompanyUrl = (id: string) => `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-2/${id}`;

const MONTH_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return y && m ? MONTH_FMT.format(new Date(Date.UTC(y, m - 1, 1))) : ym;
};
const MONTH_NAME_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' });
const monthName = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return y && m ? MONTH_NAME_FMT.format(new Date(Date.UTC(y, m - 1, 1))) : ym;
};
const DAY_FMT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
const dayLabel = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : DAY_FMT.format(d);
};
const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// The server applies the same clean-up and checks; these only catch a mistake before the request.
const normalizeWebsite = (v: string) =>
  v.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/[/?#].*$/, '').replace(/\.+$/, '');
const SITE_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const parseAmount = (v: string) => Number(v.replace(/[$,\s]/g, ''));

/** Latest stage change first, so a moved card lands at the top of its new column (the server sorts the same way). */
const byLatest = (a: PipelineDeal, b: PipelineDeal) =>
  a.stageChangedAt === b.stageChangedAt ? b.id - a.id : (a.stageChangedAt < b.stageChangedAt ? 1 : -1);

const errorText = (e: any, fallback: string): string => e?.response?.data?.error || e?.response?.data?.message || fallback;

/* ── Glide (option 1 of the preview) ─────────────────────────────────────────────────────────
   FLIP, like utils/useFlipReorder, but across columns: the moved card is drawn by a copy that
   floats above the page (so a column's scroll box cannot clip it), glides from its old spot to its
   new one, and the real card shows again when it lands with a short green ring. Every other card
   slides to its new place. transform and opacity only; nothing moves for reduced motion. */
const GLIDE_MS = 480;
const GLIDE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'; // motion.ts "emphasized"
const SETTLE_MS = 340;
const SETTLE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const ENTER_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'; // motion.ts "entrance"
const ARRIVED = 'sp-arrived';

interface FlipPlan {
  first: Map<number, DOMRect>;
  moved: number | null;
  added: number | null;
}

const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function ring(el: HTMLElement): void {
  el.classList.remove(ARRIVED);
  el.getBoundingClientRect(); // a reflow between remove and add restarts the ring
  el.classList.add(ARRIVED);
  window.setTimeout(() => el.classList.remove(ARRIVED), 1300);
}

function glide(id: number, from: DOMRect | undefined, cards: Map<number, HTMLElement>, board: HTMLElement | null, ghosts: Set<HTMLElement>): void {
  const el = cards.get(id);
  if (!el) return;
  if (!from) { ring(el); return; }
  const ghost = el.cloneNode(true) as HTMLElement;
  ghost.setAttribute('aria-hidden', 'true');
  ghost.classList.remove(ARRIVED);
  const f = pageRect(from); // [APP-FIT 2026-10-26] page px (utils/appZoom): the ghost's left / top / size and its glide
  Object.assign(ghost.style, {
    position: 'fixed', left: `${f.left}px`, top: `${f.top}px`, width: `${f.width}px`, height: `${f.height}px`,
    margin: '0', zIndex: '1100', pointerEvents: 'none', boxShadow: 'var(--sp-fly-shadow)', willChange: 'transform',
  });
  document.body.appendChild(ghost);
  ghosts.add(ghost);
  el.style.visibility = 'hidden';

  const land = () => {
    const to = pageRect(el);
    const dx = to.left - f.left;
    const dy = to.top - f.top;
    const anim = ghost.animate([
      { transform: 'translate(0px, 0px) scale(1)' },
      { transform: `translate(${(dx * 0.1).toFixed(2)}px, ${(dy * 0.1 - 8).toFixed(2)}px) scale(1.035)`, offset: 0.2 },
      { transform: `translate(${dx}px, ${dy}px) scale(1)` },
    ], { duration: GLIDE_MS, easing: GLIDE_EASE, fill: 'forwards' });
    anim.finished.catch(() => undefined).then(() => {
      ghost.remove();
      ghosts.delete(ghost);
      el.style.visibility = '';
      if (el.isConnected) ring(el);
    });
  };

  // Phones: the board swipes sideways, so bring the target column into view before the card lands.
  if (board && board.scrollWidth > board.clientWidth + 1) {
    const col = el.closest<HTMLElement>('[data-col]');
    if (col) {
      const br = board.getBoundingClientRect();
      const cr = col.getBoundingClientRect();
      if (cr.left < br.left - 1 || cr.right > br.right + 1) {
        board.scrollTo({ left: Math.max(0, col.offsetLeft - (board.clientWidth - col.offsetWidth) / 2), behavior: 'smooth' });
        window.setTimeout(land, 380);
        return;
      }
    }
  }
  land();
}

function runFlip(plan: FlipPlan, cards: Map<number, HTMLElement>, board: HTMLElement | null, ghosts: Set<HTMLElement>): void {
  // The card that moved or arrived sits at the top of its column: make sure that spot is in view.
  const lead = plan.moved ?? plan.added;
  if (lead != null) {
    const body = cards.get(lead)?.closest<HTMLElement>('[data-col-body]');
    if (body) body.scrollTop = 0;
  }
  cards.forEach((el, id) => {
    if (id === plan.moved || id === plan.added) return;
    const a = plan.first.get(id);
    if (!a) return;
    const b = el.getBoundingClientRect();
    const dx = toPage(a.left - b.left); // [APP-FIT 2026-10-26] the translate is in page px
    const dy = toPage(a.top - b.top);
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
    el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: SETTLE_MS, easing: SETTLE_EASE });
  });
  if (plan.added != null) {
    const el = cards.get(plan.added);
    if (el) {
      el.animate([{ opacity: 0, transform: 'translateY(-8px)' }, { opacity: 1, transform: 'none' }], { duration: 280, easing: ENTER_EASE });
      ring(el);
    }
  }
  if (plan.moved != null) glide(plan.moved, plan.first.get(plan.moved), cards, board, ghosts);
}

/* ── look ── */

type P = { theme: DefaultTheme };
const dark = (p: P) => p.theme.colorScheme === 'dark';

const stageInk = (p: P, stage: PipelineStage): string => {
  if (stage === 'commit') return p.theme.greenInk;
  if (stage === 'likely') return dark(p) ? '#5ec8e6' : '#0e6a85';
  if (stage === 'upside') return dark(p) ? '#f5b94a' : '#9a5a06';
  return dark(p) ? '#c9c9c9' : '#4f5653';
};

/* [PIPELINE-FULL-HEIGHT 2026-10-22, user] "the total row should end a little space just above the tab switch pill … based
   on the size increase the cards will become 3 or 4": the page fills the card ($flush in AppLayout) and never scrolls;
   the board takes all the height left, so a taller screen shows more cards. 84 px = the dock's reach over the card
   (66 px) + ~18 px of space, as on Campaign History. */
const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 0 16px 84px;
  color: ${(p) => p.theme.text};

  /* [PHONE-LAYOUT 2026-10-23, user] Phones: NOTHING is fixed at the top — the whole page (title, switch, the two boxes,
     the add line, the board) is ONE scroll ("in the phone view nothing will be fixed on the top side otherwise how will
     i see the data for the below"). The dock clearance is the panes' own end padding (PaneViewport). */
  @media (max-width: 768px) {
    padding: 0 10px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

const Toolbar = styled.div`
  flex-shrink: 0; /* [PIPELINE-FULL-HEIGHT] */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 2px;
  border-bottom: 1px solid ${(p) => p.theme.line};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    /* [PHONE-LAYOUT 2026-10-22] a little tighter: title, switch and the two boxes stay in view above the panes */
    gap: 8px;
    padding: 10px 2px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;

  @media (max-width: 768px) {
    text-align: center;
    /* [PHONE-LAYOUT 2026-10-22] the shared phone page title */
    font-size: 17px;
    font-weight: 800;
    line-height: 1.2;
  }
`;

/* [REP-WISE 2026-10-13] $solo: Rep wise data, where the Sales Rep box is gone — on a phone the Month box then takes
   the whole row under the switch. */
const Filters = styled.div<{ $solo?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    & > button { min-width: 0; width: 100%; }
    ${(p) => (p.$solo ? '& > button { grid-column: 1 / -1; }' : '')}
  }
`;

const Entry = styled.form`
  flex-shrink: 0; /* [PIPELINE-FULL-HEIGHT] */
  display: grid;
  /* [CLOSE-DATE 2026-10-12] + the Tentative Close Date box before Save. Deal Stage never narrower than
     "Early Pipeline (15%)", the date box never narrower than "Oct 31, 2026" (measured down to 1181 px). */
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.35fr) minmax(0, 0.95fr) minmax(0, 1.1fr) minmax(0, 0.8fr) minmax(176px, 1.25fr) minmax(150px, 1.1fr) auto;
  gap: 10px;
  align-items: end;
  padding: 14px 2px 4px;

  ${layoutBetween(769, 1180)} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    /* [PHONE-LAYOUT 2026-10-22] Phones, so no box cuts its text: Deal Owner takes a whole row right after the email
       ("Pick a Sales Rep above" or a long name in full), Payment Type | Amount share a row, Deal Stage gets a whole row
       ("Early Pipeline (15%)" in full), and Tentative Close Date | Save share the last one. Same six rows as before.
       Children in order (the loading view has the same eight): URL, Email, Type, Owner, Amount, Stage, Close Date, Save. */
    & > :nth-child(-n + 2) { order: -2; }
    & > :nth-child(4) { order: -1; grid-column: 1 / -1; }
    & > :nth-child(6) { grid-column: 1 / -1; }
    & > :nth-child(7),
    & > :nth-child(8) { grid-column: auto; }
  }
`;

const Field = styled.div<{ $wide?: boolean; $save?: boolean }>`
  display: grid;
  gap: 5px;
  min-width: 0;

  ${layoutBetween(769, 1180)} {
    ${(p) => (p.$save ? 'grid-column: 3; justify-self: end;' : '')}
  }
  @media (max-width: 768px) {
    ${(p) => (p.$wide || p.$save ? 'grid-column: 1 / -1;' : '')}
    ${(p) => (p.$save ? '& > span:first-child { display: none; }' : '')}
  }
`;

const Label = styled.label`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
`;

const badRing = (p: P) => (dark(p) ? 'rgba(248,113,113,0.16)' : 'rgba(200,30,30,0.10)');
const focusRing = (p: P) => (dark(p) ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)');

const Input = styled.input<{ $bad?: boolean }>`
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.text};
  border: 1px solid ${(p) => (p.$bad ? p.theme.danger : dark(p) ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')};
  background: ${(p) => (dark(p) ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)')};
  box-shadow: ${(p) => (p.$bad ? `0 0 0 3px ${badRing(p)}` : 'none')};
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &::placeholder { color: ${(p) => p.theme.dim}; }
  &:focus {
    outline: none;
    border-color: ${(p) => (p.$bad ? p.theme.danger : p.theme.green600)};
    box-shadow: 0 0 0 3px ${(p) => (p.$bad ? badRing(p) : focusRing(p))};
  }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSunken}
  ${(p) => (p.$bad ? `border-color: ${p.theme.danger}; box-shadow: 0 0 0 3px ${badRing(p)};` : '')}
`;

const Money = styled.div`
  position: relative;

  & > span {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    font-weight: 700;
    pointer-events: none;
  }
  & > input {
    padding-left: 22px;
    font-variant-numeric: tabular-nums;
  }
`;

/* GlassSelect as a form box: same height and look as the text boxes (the Split popup's PmSelect recipe). */
const BoxSelect = styled(GlassSelect)<{ $bad?: boolean }>`
  && {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    height: 36px;
    padding: 0 30px 0 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    border: 1px solid ${(p) => (p.$bad ? p.theme.danger : dark(p) ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')};
    background: ${(p) => (dark(p) ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)')};
    background-image: none;
    box-shadow: ${(p) => (p.$bad ? `0 0 0 3px ${badRing(p)}` : 'none')};
  }
  &&[aria-expanded="true"],
  &&:focus-visible {
    outline: none;
    border-color: ${(p) => p.theme.green600};
    box-shadow: 0 0 0 3px ${focusRing};
  }
`;

/* [CLOSE-DATE 2026-10-12, user] The app's own calendar (SingleDateCalendar), its button dressed as an add-line box. */
const DateBox = styled.div<{ $bad?: boolean; $off?: boolean }>`
  min-width: 0;
  ${(p) => (p.$off ? 'opacity: 0.55; pointer-events: none;' : '')}

  & > button {
    width: 100%;
    height: 36px;
    box-sizing: border-box;
    padding: 0 12px;
    gap: 8px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    border: 1px solid ${(p) => (p.$bad ? p.theme.danger : dark(p) ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')};
    background: ${(p) => (dark(p) ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)')};
    box-shadow: ${(p) => (p.$bad ? `0 0 0 3px ${badRing(p)}` : 'none')};
  }
  & > button > i.chev { margin-left: auto; }
`;

/* [CLOSE-DATE 2026-10-12, user] The card's "Tentative Close Date" line; on an open month the date is the calendar's
   button, small, so it can be changed right there. */
const CardDate = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 6px;
  font-size: 12px;

  & > button {
    height: 24px;
    padding: 0 8px;
    gap: 6px;
    border-radius: 7px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
  }
  & > button > i.cal,
  & > button > i.chev { font-size: 10px; }

  /* [PHONE-LAYOUT 2026-10-22] phones: the card's date button a little taller (easier to tap) */
  @media (max-width: 768px) {
    & > button { height: 28px; }
  }
`;

const Locked = styled.div<{ $bad?: boolean; $empty?: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px ${(p) => (p.$bad ? 'solid' : 'dashed')} ${(p) => (p.$bad ? p.theme.danger : dark(p) ? 'rgba(255,255,255,0.20)' : 'rgba(22,25,23,0.18)')};
  box-shadow: ${(p) => (p.$bad ? `0 0 0 3px ${badRing(p)}` : 'none')};
  background: ${(p) => p.theme.pill};
  font-size: 13px;
  font-weight: 600;
  min-width: 0;

  & > i { font-size: 11px; }
  & > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${(p) => (p.$empty ? p.theme.dim : 'inherit')};
    font-weight: ${(p) => (p.$empty ? 500 : 600)};
  }
`;

const SaveButton = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
  height: 36px;
  padding: 0 22px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormMsg = styled.p`
  flex-shrink: 0; /* [PIPELINE-FULL-HEIGHT] */
  margin: 6px 2px 0;
  font-size: 12.5px;
  font-weight: 700;
  color: ${(p) => p.theme.danger};
`;

const Notice = styled.p`
  flex-shrink: 0; /* [PIPELINE-FULL-HEIGHT] */
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 2px 0;
  font-size: 12.5px;
  font-weight: 600;

  & > i { color: ${(p) => p.theme.greenInk}; }
`;

const BoardHead = styled.div`
  flex-shrink: 0; /* [PIPELINE-FULL-HEIGHT] */
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 16px;
  padding: 14px 2px 10px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  b { font-weight: 800; }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    /* [PHONE-LAYOUT 2026-10-22] two lines, 58 px in all (12 + 18 + 2 + 18 + 8): the Board below is sized with it */
    gap: 2px 16px;
    padding: 12px 2px 8px;
    line-height: 18px;
  }
`;

/* [PIPELINE-FULL-HEIGHT 2026-10-22] All the height left under the line above it; the columns stretch to its bottom. */
const Board = styled.div<{ $dim?: boolean }>`
  position: relative;
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(236px, 1fr));
  grid-template-rows: minmax(0, 1fr);
  gap: 10px;
  overflow-x: auto;
  opacity: ${(p) => (p.$dim ? 0.6 : 1)};
  transition: opacity 180ms cubic-bezier(0.4, 0, 0.2, 1);

  /* [PHONE-LAYOUT 2026-10-22] Phones: the Pipeline View pane scrolls (the add line goes up and away) and the board is
     as tall as that pane less the 58 px line above it, so at the pane's end that line and the four columns fill the
     screen above the dock. One column at a time: each 84 % wide, snapping into the middle, the next one peeking. */
  /* [PHONE-LAYOUT 2026-10-23, user] ...and each column is as tall as its cards (no box of its own to scroll in): the page
     scroll shows every card; a sideways swipe still moves from one column to the next. */
  @media (max-width: 768px) {
    flex: none;
    height: auto;
    min-height: 0;
    grid-template-rows: auto;
    align-items: start;
    grid-template-columns: repeat(4, 84%);
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

const Column = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0; /* [PIPELINE-FULL-HEIGHT] */
  border-radius: 12px;
  background: ${(p) => (dark(p) ? '#161616' : '#f3f5f4')};
  border: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.07)')};

  @media (max-width: 768px) {
    scroll-snap-align: center;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.04' : '0.6')}), rgba(255,255,255,0) 30%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.06' : '0.9')});
`;

const ColumnHead = styled.header`
  flex-shrink: 0; /* [PIPELINE-FULL-HEIGHT] */
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 12px 9px;
`;

const Dot = styled.span<{ $stage: PipelineStage }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${(p) => stageInk(p, p.$stage)};
`;

const ColumnName = styled.span`
  font-size: 13.5px;
  font-weight: 800;
`;

const Count = styled.span`
  margin-left: auto;
  min-width: 24px;
  height: 22px;
  padding: 0 7px;
  box-sizing: border-box;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.07)')};
`;

/* [PIPELINE-FULL-HEIGHT 2026-10-22] Fills the column between its head and the Total row (was capped at 470 px, which
   left a blank band above the dock on a big screen); scrolls by itself. */
const ColumnBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 2px 8px 10px;
  overflow-y: auto;
  overscroll-behavior: contain;

  /* [PHONE-LAYOUT 2026-10-22] [PHONE-LAYOUT 2026-10-23, user] Phones: the column shows all its cards and the page
     scrolls, so the board never traps the finger. */
  @media (max-width: 768px) {
    /* [PHONE-LAYOUT 2026-10-23, user] no inner scroll on phones: every card shows, the page scrolls */
    flex: none;
    min-height: auto;
    overflow-y: visible;
  }
`;

const Empty = styled.div`
  display: grid;
  place-items: center;
  min-height: 96px;
  padding: 10px;
  border: 1.5px dashed ${(p) => (dark(p) ? 'rgba(255,255,255,0.18)' : 'rgba(22,25,23,0.16)')};
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 600;
  text-align: center;
`;

const ColumnFoot = styled.footer`
  flex-shrink: 0; /* [PIPELINE-FULL-HEIGHT] */
  padding: 9px 12px 10px;
  border-top: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.07)')};
  font-size: 12px;
  font-variant-numeric: tabular-nums;

  b { font-weight: 800; }
`;

const arrive = keyframes`
  0% { opacity: 0; }
  12% { opacity: 1; }
  70% { opacity: 1; }
  100% { opacity: 0; }
`;

/* --sp-fly-shadow is read by the gliding copy (it carries these classes).
   [PAID-GREEN / DECLINE 2026-10-07, user] $tone: 'paid' = green (New Sale money received this month reached the
   amount), 'declined' = red (marked declined / refused). Solid colours, so the gliding copy is never see-through. */
type CardTone = 'paid' | 'declined' | null;
const cardFill = (p: P & { $tone?: CardTone }) => {
  if (p.$tone === 'paid') return dark(p) ? '#273b32' : '#e5f4f0';
  if (p.$tone === 'declined') return dark(p) ? '#3d2c2c' : '#fbefef';
  return dark(p) ? '#232323' : '#ffffff';
};
const cardLine = (p: P & { $tone?: CardTone }) => {
  if (p.$tone === 'paid') return `1.5px solid ${p.theme.greenInk}`;
  if (p.$tone === 'declined') return `1.5px solid ${p.theme.danger}`;
  return `1px solid ${dark(p) ? 'rgba(255,255,255,0.09)' : 'rgba(22,25,23,0.09)'}`;
};
const Card = styled.article<{ $tone?: CardTone }>`
  --sp-fly-shadow: ${(p) => (dark(p) ? '0 18px 40px rgba(0,0,0,0.6)' : '0 18px 40px rgba(26,33,30,0.22)')};
  position: relative;
  display: grid;
  gap: 3px;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 10px;
  color: ${(p) => p.theme.text};
  background: ${cardFill};
  border: ${cardLine};
  box-shadow: ${(p) => (dark(p) ? '0 1px 2px rgba(0,0,0,0.4)' : '0 1px 2px rgba(26,33,30,0.06)')};

  &.${ARRIVED}::after {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 10px;
    border: 2px solid ${(p) => p.theme.greenInk};
    box-shadow: 0 0 0 4px ${(p) => p.theme.greenTint};
    pointer-events: none;
    animation: ${arrive} 1200ms cubic-bezier(0.4, 0, 0.2, 1) both;
  }
  @media (prefers-reduced-motion: reduce) {
    &.${ARRIVED}::after { animation: none; opacity: 0; }
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.06' : '0.7')}), rgba(255,255,255,0) 55%);
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  /* [PHONE-LAYOUT 2026-10-22] phones: when the website and the type pill do not fit one line, the pill moves under the
     website (a long website was broken mid-word beside it: "plumbingeducation.co" / "m"). */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    row-gap: 6px;
  }
`;

const Site = styled.span`
  min-width: 0;
  font-size: 14px;
  font-weight: 800;
  color: ${(p) => p.theme.greenInk};
  overflow-wrap: anywhere;
`;

const TypePill = styled.span<{ $recurring: boolean }>`
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.2px;
  background: ${(p) => (p.$recurring ? p.theme.greenTint : p.theme.pill)};
  color: ${(p) => (p.$recurring ? p.theme.greenInk : p.theme.text)};

  /* [PHONE-LAYOUT 2026-10-22] phones: labels at least 11 px */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const Amount = styled.div`
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 0.1px;
  font-variant-numeric: tabular-nums;
`;

/* [CARD-EDIT 2026-10-07, user] The amount is a button on an open month: click it to change the amount. */
const AmountButton = styled.button`
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 0.1px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.text};
  cursor: pointer;

  & > i { font-size: 10px; opacity: 0.75; transition: opacity 120ms cubic-bezier(0.4, 0, 0.2, 1); }
  &:hover > i, &:focus-visible > i { opacity: 1; }
  /* [PHONE-LAYOUT 2026-10-22] phones: an invisible larger tap area (about 38 px tall) around the 22 px amount, so a finger
     opens the amount edit reliably; the look and the card's height do not change. */
  @media (max-width: 768px) {
    position: relative;
    &::after { content: ''; position: absolute; inset: -8px -6px; }
  }
`;

const AmountEdit = styled.form`
  display: flex;
  align-items: center;
  gap: 5px;

  & > span { font-size: 15px; font-weight: 800; }
  & > input {
    width: 110px;
    min-width: 0;
    height: 30px;
    box-sizing: border-box;
    padding: 0 8px;
    border-radius: 7px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${(p) => p.theme.text};
    border: 1px solid ${(p) => p.theme.green600};
    background: ${(p) => (dark(p) ? 'rgba(255,255,255,0.06)' : '#ffffff')};
    box-shadow: 0 0 0 3px ${focusRing};
    outline: none;
  }
`;

const IconButton = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  ${lgOnWhite}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 7px;
  font-size: 11px;

  /* [PHONE-LAYOUT 2026-10-22] phones: easier to tap */
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

/* [CARD-EDIT 2026-10-07, user] The Recurring / One-Time tag as a small dropdown on an open month. */
const TypeSelect = styled(GlassSelect)<{ $recurring: boolean }>`
  && {
    flex: 0 0 auto;
    height: 22px;
    min-width: 0;
    padding: 0 20px 0 9px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.2px;
    background: ${(p) => (p.$recurring ? p.theme.greenTint : p.theme.pill)};
    background-image: none;
    box-shadow: none;
    color: ${(p) => (p.$recurring ? p.theme.greenInk : p.theme.text)};
  }
  && > span:last-child { right: 5px; }
  &&[aria-expanded="true"],
  &&:focus-visible {
    outline: none;
    border-color: ${(p) => p.theme.green600};
  }

  /* [PHONE-LAYOUT 2026-10-22] phones: a taller tag (easier to tap), labels at least 11 px */
  @media (max-width: 768px) {
    && {
      height: 28px;
      font-size: 11px;
    }
  }
`;

const Line = styled.div<{ $small?: boolean }>`
  font-size: ${(p) => (p.$small ? '12px' : '12.5px')};
  overflow-wrap: anywhere;
`;

/* [CARD-ACTIVITY 2026-10-07, user] Under "Added": one line for HubSpot, one for Google Meet (option 1 of the preview). */
const ActivityLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 3px 10px;
  margin-top: 3px;
  font-size: 12px;
  font-weight: 700;

  & > b { font-weight: 800; }
  & > span { display: inline-flex; align-items: center; gap: 5px; }
  & > span > i { font-size: 11px; color: ${(p) => p.theme.greenInk}; }
`;

const ActivityNone = styled.div`
  margin-top: 3px;
  font-size: 12px;
  font-weight: 600;
`;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The two activity lines of a card; skeleton lines until the numbers arrive (same height, so nothing jumps). */
const CardActivityLines: React.FC<{ a: PipelineCardActivity | undefined; inHubSpot: boolean; month: string }> = ({ a, inHubSpot, month }) => {
  if (!a) {
    return (
      <>
        {inHubSpot && <SkeletonBox $w="50%" $h="12px" $mt="6px" />}
        <SkeletonBox $w="58%" $h="12px" $mt="6px" />
      </>
    );
  }
  const name = monthName(month);
  return (
    <>
      {/* [NO-EMAILS 2026-10-12, user] "from the hubspot row, remove the email sent part, i dont need that thing":
          calls only (the email count was always 0 — HubSpot gives our tokens no sender on emails). */}
      {a.hubspot && (a.hubspot.calls ? (
        <ActivityLine data-activity="hubspot">
          <b>HubSpot:</b>
          <span><i className="fas fa-phone" aria-hidden="true" />{plural(a.hubspot.calls, 'call made', 'calls made')}</span>
        </ActivityLine>
      ) : (
        <ActivityNone data-activity="hubspot">HubSpot: no calls in {name}</ActivityNone>
      ))}
      {a.meet.held || a.meet.scheduled ? (
        <ActivityLine data-activity="meet">
          <b>Google Meet:</b>
          <span><i className="fas fa-video" aria-hidden="true" />{a.meet.held} held</span>
          <span><i className="fas fa-calendar" aria-hidden="true" />{a.meet.scheduled} scheduled</span>
        </ActivityLine>
      ) : (
        <ActivityNone data-activity="meet">Google Meet: no meetings in {name}</ActivityNone>
      )}
    </>
  );
};

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.09)' : 'rgba(22,25,23,0.09)')};
`;

/* The stage dropdown and, on its right, the Decline button. */
const ActionsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/* [DECLINE 2026-10-07, user] Marks the card declined / refused (red); pressed again it undoes it. */
const DeclineButton = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $on: boolean }>`
  ${lgButtonSecondary}
  ${lgOnWhite}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
  height: 28px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12px;
  gap: 5px;
  color: ${(p) => p.theme.danger};
  ${(p) => (p.$on ? `border-color: ${p.theme.danger}; background: ${dark(p) ? 'rgba(248,113,113,0.16)' : 'rgba(200,30,30,0.08)'};` : '')}

  & > i { font-size: 10px; }

  /* [PHONE-LAYOUT 2026-10-22] phones: the card's buttons 32 px tall (easier to tap) */
  @media (max-width: 768px) {
    height: 32px;
  }
`;

const StageSelect = styled(GlassSelect)`
  && {
    height: 28px;
    min-width: 0;
    padding: 0 26px 0 9px;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 700;
  }

  /* [PHONE-LAYOUT 2026-10-22] phones: the card's buttons 32 px tall (easier to tap) */
  @media (max-width: 768px) {
    && { height: 32px; }
  }
`;

const HubSpotLink = styled.a.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  ${lgOnWhite}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
  height: 28px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12px;
  gap: 5px;

  & > i { font-size: 10px; }

  /* [PHONE-LAYOUT 2026-10-22] phones: the card's buttons 32 px tall (easier to tap) */
  @media (max-width: 768px) {
    height: 32px;
  }
`;

const NotInHubSpot = styled.span`
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px dashed ${(p) => (dark(p) ? 'rgba(255,255,255,0.20)' : 'rgba(22,25,23,0.18)')};
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
`;

const Plain = styled.p`
  margin: 24px 2px;
  font-size: 13px;
  font-weight: 600;
`;

/* ── [LOADING-FORM 2026-10-07, user] Loading view of the toolbar dropdowns and the add line ──
   Same boxes, same sizes as the real controls, so the page does not move when the data arrives. */

/** A skeleton bar exactly one text line tall: it carries an invisible "M" in the parent's font. */
const TextLine = styled.span`
  position: relative;
  display: inline-block;
  color: transparent;
  user-select: none;

  & > div {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const FilterSkeleton = styled(SkeletonBox)`
  && {
    width: 150px;
    height: 33px; /* a GlassSelect trigger is 33px tall (measured) */
    border-radius: 8px;
  }
  @media (max-width: 768px) {
    && { width: 100%; }
    /* [PHONE-LAYOUT 2026-10-22] a phone GlassSelect trigger is 36 px tall (9 px top and bottom) */
    && { height: 36px; }
  }
`;

const SaveSkeleton = styled(SkeletonBox)`
  && {
    width: 88px;
    height: 36px;
    border-radius: 10px;
  }
  @media (max-width: 768px) {
    && { width: 100%; }
  }
`;

/** The add line's six boxes: label bar width, and whether the box spans the row on phones (like the real ones). */
const ENTRY_SKELETON: { key: string; label: string; wide: boolean }[] = [
  { key: 'website', label: '86px', wide: true },
  { key: 'email', label: '92px', wide: true },
  { key: 'type', label: '84px', wide: false },
  { key: 'owner', label: '72px', wide: false },
  { key: 'amount', label: '52px', wide: false },
  { key: 'stage', label: '70px', wide: false },
  { key: 'closeDate', label: '128px', wide: true }, // [CLOSE-DATE 2026-10-12]
];

/* ── [SUGGEST 2026-10-07, user] HubSpot suggestions under the Company URL / Company Email boxes ──
   The list floats above the page (a portal, like GlassSelect's) so nothing clips it. */

const SuggestPop = styled.div<{ $open: boolean }>`
  position: fixed;
  z-index: 1200;
  max-height: 320px;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 6px;
  border-radius: 12px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, ${(p) => (dark(p) ? 0.08 : 0.4)}) 0%, rgba(255, 255, 255, 0) 40%),
    ${(p) => (dark(p) ? 'rgba(38, 38, 38, 0.985)' : 'rgba(250, 251, 253, 0.985)')};
  border: 1px solid ${(p) => (dark(p) ? 'rgba(255, 255, 255, 0.12)' : 'rgba(22, 25, 23, 0.10)')};
  box-shadow: 0 16px 40px rgba(15, 23, 42, ${(p) => (dark(p) ? 0.55 : 0.18)}), 0 2px 6px rgba(15, 23, 42, ${(p) => (dark(p) ? 0.4 : 0.1)});
  /* [FLUID-1 2026-10-12, user] Grows down out of the box's left corner on the spring, the suggestion
     rows rising in one after another (components/Fluid.tsx); the close plays before it unmounts. */
  ${(p) => fluidPop({ open: p.$open, origin: '0 0', from: 'translateY(-8px) scale(0.94)' })}
  ${(p) => (p.$open ? fluidCascade('[role="option"]') : '')}
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: ${lgCardSheen};
`;

const SuggestRow = styled.div<{ $active: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 8px;
  row-gap: 1px;
  padding: 7px 10px;
  border-radius: 8px;
  cursor: pointer;
  color: ${(p) => p.theme.text};
  background: ${(p) => (p.$active ? (dark(p) ? 'rgba(14, 126, 78, 0.30)' : 'rgba(26, 127, 100, 0.14)') : 'transparent')};

  & > .main { font-size: 13px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  & > .tag { font-size: 10.5px; font-weight: 800; padding: 1px 7px; border-radius: 999px; background: ${(p) => p.theme.greenTint}; color: ${(p) => p.theme.greenInk}; }
  & > .sub { grid-column: 1 / -1; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

interface SuggestItem {
  key: string;
  main: string;
  sub?: string | null;
  tag?: string | null;
  /** Website rows: the company's HubSpot email, used to fill an empty Company Email box. */
  fill?: string | null;
}

/** A text box with HubSpot suggestions under it: asks the server ~0.2 s after typing stops, newest answer wins,
 *  arrow keys + Enter or a click to pick, Escape to close. Free typing still works for anything not in HubSpot. */
const SuggestBox: React.FC<{
  id: string;
  value: string;
  placeholder: string;
  type?: string;
  disabled: boolean;
  bad: boolean;
  minChars: number;
  /** Open on focus / when empty too (the email box once a website is chosen). */
  showWhenEmpty?: boolean;
  onChange: (v: string) => void;
  load: (q: string) => Promise<SuggestItem[]>;
  onPick: (item: SuggestItem) => void;
}> = ({ id, value, placeholder, type, disabled, bad, minChars, showWhenEmpty, onChange, load, onPick }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<SuggestItem[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const pres = usePresence(open, fluid.outMs); // [FLUID-1 2026-10-12] mounted through the 200 ms close
  const seq = useRef(0);
  const timer = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  const close = () => {
    seq.current += 1;
    window.clearTimeout(timer.current);
    setOpen(false);
    setActive(-1);
  };
  const ask = (q: string, waitMs: number) => {
    window.clearTimeout(timer.current);
    const mine = ++seq.current;
    timer.current = window.setTimeout(async () => {
      try {
        const found = await loadRef.current(q);
        if (mine !== seq.current) return;
        setItems(found);
        setActive(-1);
        const r = inputRef.current ? pageRect(inputRef.current) : null; // [APP-FIT 2026-10-26] page px, like the list's left / top
        if (r) setPos({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 300) });
        setOpen(found.length > 0 && document.activeElement === inputRef.current);
      } catch {
        if (mine === seq.current) setOpen(false);
      }
    }, waitMs);
  };
  const wants = (q: string) => q.trim().length >= minChars || !!showWhenEmpty;
  const pick = (item: SuggestItem) => {
    close();
    onPick(item);
  };

  // The page scrolls inside its card: a list left behind would point at the wrong box, so it closes.
  useEffect(() => {
    if (!open) return undefined;
    const shut = (e: Event) => {
      if (e.type === 'scroll' && listRef.current && e.target instanceof Node && listRef.current.contains(e.target)) return;
      seq.current += 1;
      setOpen(false);
      setActive(-1);
    };
    window.addEventListener('scroll', shut, true);
    window.addEventListener('resize', shut);
    return () => {
      window.removeEventListener('scroll', shut, true);
      window.removeEventListener('resize', shut);
    };
  }, [open]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <>
      <Input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        $bad={bad}
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? `${id}-opt-${active}` : undefined}
        onChange={(e) => {
          onChange(e.target.value);
          if (wants(e.target.value)) ask(e.target.value.trim(), 220);
          else close();
        }}
        onFocus={() => { if (wants(value)) ask(value.trim(), 0); }}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open || !items.length) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % items.length); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i <= 0 ? items.length - 1 : i - 1)); }
          else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(items[active]); }
          else if (e.key === 'Escape' || e.key === 'Tab') close();
        }}
      />
      {pres.mounted && pos && createPortal(
        <SuggestPop
          ref={listRef}
          $open={open && pres.entered}
          id={`${id}-list`}
          role="listbox"
          style={{ left: pos.left, top: pos.top, width: pos.width }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {items.map((it, i) => (
            <SuggestRow
              key={it.key}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              $active={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(it)}
            >
              <span className="main">{it.main}</span>
              {it.tag ? <span className="tag">{it.tag}</span> : <span />}
              {it.sub && <span className="sub">{it.sub}</span>}
            </SuggestRow>
          ))}
        </SuggestPop>,
        document.body,
      )}
    </>
  );
};

/* ── page ── */

/* ── [REP-WISE 2026-10-13, user] "before the month filter … a toggle switch … 'Main Screen' and … 'Rep wise data'; on the
   rep wise data … a table which will have rep names and then the 4 sections … the amount values in those columns". The
   user's answers: every rep at once, a Total column + a Total row, and declined cards get "a box for that too" — their own
   column, left out of the four sections and the Total (as on the board's totals). ── */
const VIEWS = ['main', 'rep'] as const;
type PipelineView = typeof VIEWS[number];
// [PIPELINE-VIEW-LABEL 2026-10-22, user] "change main screen to 'Pipeline View'" — the label only; the key stays 'main'.
const VIEW_LABEL: Record<PipelineView, string> = { main: 'Pipeline View', rep: 'Rep wise data' };

/* [PIPELINE-FULL-HEIGHT 2026-10-22] The views take the rest of the page under the toolbar: Pipeline View is a column (add
   line, the line above the board, then the board filling the rest); Rep wise data scrolls inside its own pane. */
const PaneViewport = styled(SlideViewport)`
  flex: 1;
  min-height: 0;

  & > [data-pipeline-pane] {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  & > [data-pipeline-pane='rep'] {
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  /* [PHONE-LAYOUT 2026-10-23, user] Phones: the panes do NOT scroll by themselves any more — they take their full height
     and the page scrolls (see Page). 80 px of end padding = the dock's reach over the card (about 64 px) + 16 px, so the
     board's Total rows and the rep table's last row end just above the dock. */
  @media (max-width: 768px) {
    flex: none;
    min-height: auto;
    & > [data-pipeline-pane],
    & > [data-pipeline-pane='rep'] {
      display: block;
      height: auto;
      overflow: visible;
      padding-bottom: 80px;
    }
  }
`;

/* The app's switch pill (styles/buttons pillTrack / pillSegment) with the Sales Scoreboard's sliding knob. */
const Pill = styled.div`
  position: relative;
  display: inline-flex;
  align-items: stretch;
  ${pillTrack}
  background: ${(p) => (dark(p) ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.05)')};
  border: 1px solid ${(p) => p.theme.line};

  /* [REP-WISE 2026-10-13] Last on a desktop (the right end); on a phone it keeps its own row ABOVE the boxes. */
  @media (max-width: 768px) {
    grid-column: 1 / -1;
    justify-self: center;
    order: -1;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border-color: transparent;
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)' : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};
`;
const PillKnob = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: ${(p) => p.$width}px;
  transform: translateX(${(p) => p.$left}px);
  border-radius: 999px;
  background: ${(p) => (dark(p) ? '#4acd8b' : p.theme.green600)};
  transition: transform 240ms cubic-bezier(0.4, 0, 0.2, 1), width 240ms cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(p) => (p.$width ? 1 : 0)};
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.30)' : 'rgba(20,107,84,0.30)')}, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.12);
`;
/* [REP-WISE 2026-10-13, user] "on the rep wise, remove all sales rep dropdown, no need there": gone while Rep wise data is
   on show (no empty place left). The switch is the LAST box ("make this toggle on the right side … so that it will remain
   on its place while switching") and this one the FIRST ("all sales rep will be left"), so when it goes or comes back
   the Month box and the switch both keep their places. */
const RepPick = styled(GlassSelect)<{ $off?: boolean }>`
  ${(p) => (p.$off ? 'display: none;' : '')}
`;

const PillOpt = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  border: none;
  background: transparent;
  cursor: pointer;
  ${pillSegment}
  color: ${(p) => (p.$active ? (dark(p) ? '#06281d' : '#ffffff') : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    padding: 7px 12px;
    font-size: 12px;
  }
`;

/** The knob moves on the press; the page follows as a transition, and a second press while it catches up is ignored
 *  (the Sales Scoreboard's PeriodPill — feedback "toggles quiet + instant"). */
const ViewPill: React.FC<{ view: PipelineView; onPick: (v: PipelineView) => void }> = ({ view, onPick }) => {
  const [picked, setPicked] = useState<PipelineView | null>(null);
  const [pending, startPick] = useTransition();
  useEffect(() => { if (!pending) setPicked(null); }, [pending, view]);
  const shown = picked ?? view;
  const optRefs = useRef<Partial<Record<PipelineView, HTMLButtonElement | null>>>({});
  const [knob, setKnob] = useState({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const el = optRefs.current[shown];
    if (el) setKnob({ left: el.offsetLeft, width: el.offsetWidth });
  }, [shown]);
  const pick = (v: PipelineView) => {
    if (v === shown || pending) return;
    setPicked(v);
    startPick(() => onPick(v));
  };
  const pressedRef = useRef(false);
  const press = (v: PipelineView) => ({
    onPointerDown: (e: React.PointerEvent) => {
      pressedRef.current = isPlainMousePress(e);
      if (pressedRef.current) pick(v);
    },
    onClick: (e: React.MouseEvent) => {
      if (clickEndsPress(pressedRef, e)) return; // the press already switched
      pick(v);
    },
  });
  return (
    <Pill role="tablist" aria-label="Sales Pipeline view">
      <PillKnob $left={knob.left} $width={knob.width} />
      {VIEWS.map((v) => (
        <PillOpt
          key={v}
          ref={(el) => { optRefs.current[v] = el; }}
          type="button"
          role="tab"
          aria-selected={shown === v}
          $active={shown === v}
          {...press(v)}
        >
          {VIEW_LABEL[v]}
        </PillOpt>
      ))}
    </Pill>
  );
};

interface RepRow { owner: string; stages: Record<PipelineStage, number>; total: number; declined: number }
const noStages = (): Record<PipelineStage, number> => ({ commit: 0, likely: 0, upside: 0, early: 0 });

/** One row per Deal Owner with a card in the month, biggest Total first. A declined card counts only in Declined. */
function repRowsOf(items: PipelineDeal[]): RepRow[] {
  const by = new Map<string, RepRow>();
  for (const d of items) {
    const owner = d.owner || '—';
    let r = by.get(owner);
    if (!r) { r = { owner, stages: noStages(), total: 0, declined: 0 }; by.set(owner, r); }
    if (d.declined) r.declined += d.amount;
    else if (d.stage in r.stages) { r.stages[d.stage] += d.amount; r.total += d.amount; }
  }
  return Array.from(by.values()).sort((a, b) => b.total - a.total || a.owner.localeCompare(b.owner));
}

function repTotal(rows: RepRow[]): RepRow {
  const t: RepRow = { owner: 'Total', stages: noStages(), total: 0, declined: 0 };
  for (const r of rows) {
    for (const s of STAGES) t.stages[s.key] += r.stages[s.key];
    t.total += r.total;
    t.declined += r.declined;
  }
  return t;
}

/* Same card surface as the board's columns; a phone swipes the table sideways inside it. */
const RepCard = styled.div`
  flex-shrink: 0; /* [PIPELINE-FULL-HEIGHT] its pane scrolls, not the card */
  border-radius: 12px;
  background: ${(p) => (dark(p) ? '#161616' : '#f3f5f4')};
  border: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.07)')};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
`;
const RepTable = styled.table`
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  th, td { padding: 11px 14px; white-space: nowrap; text-align: right; }
  th:first-child, td:first-child { text-align: left; }
  thead th { font-size: 13px; font-weight: 800; border-bottom: 1px solid ${(p) => p.theme.line}; }
  tbody td:first-child { font-weight: 700; }
  tbody tr + tr td { border-top: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.05)' : 'rgba(22,25,23,0.06)')}; }
  tfoot td { font-weight: 800; border-top: 1px solid ${(p) => p.theme.line}; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: the table is as wide as its columns and swipes sideways inside its card; phone
     cell padding (12 px at the card's edges). */
  @media (max-width: 768px) {
    width: max-content;
    min-width: 100%;
    th, td { padding: 8px 10px; }
    th:first-child, td:first-child { padding-left: 12px; }
    th:last-child, td:last-child { padding-right: 12px; }
  }
`;
const HeadIn = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
`;
const DeclinedDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${(p) => p.theme.danger};
`;
const Nil = styled.span`
  color: ${(p) => p.theme.muted};
`;
const DeclinedAmt = styled.span`
  color: ${(p) => p.theme.danger};
  font-weight: 700;
`;
const RepEmpty = styled.div`
  padding: 18px 14px 20px;
  text-align: center;
  font-size: 12.5px;
  font-weight: 600;
`;

const amountCell = (n: number) => (n ? money(n) : <Nil>—</Nil>);
const declinedCell = (n: number) => (n ? <DeclinedAmt>{money(n)}</DeclinedAmt> : <Nil>—</Nil>);
const REP_SKELETON_ROWS = [0, 1, 2, 3];
const cellSkeleton = (w: string) => (
  <SkeletonBox as="span" $w={w} $h="12px" $radius="6px" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
);

/** `rows` null = still loading (skeleton); `landKey` changes when new data LANDS (a visit, a month pick, the every-rep
 *  answer), so the rows rise then — never because the switch was pressed (its pane is $quiet). */
const RepWiseTable: React.FC<{ month: string; rows: RepRow[] | null; failed: boolean; onRetry: () => void; landKey: string | null }> = ({
  month, rows, failed, onRetry, landKey,
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  useRowsLand(tableRef, landKey);
  const sum = useMemo(() => (rows ? repTotal(rows) : null), [rows]);
  if (failed && !rows) return <LoadFailed what="the rep wise data" onRetry={onRetry} />;
  return (
    <>
      {rows && sum ? (
        <BoardHead>
          <div><b>{monthLabel(month)}</b> · {rows.length} {rows.length === 1 ? 'Sales Rep' : 'Sales Reps'}</div>
          <div>Pipeline total <b>{money(sum.total)}</b></div>
        </BoardHead>
      ) : (
        <BoardHead aria-hidden="true">
          <div><TextLine style={{ width: '220px' }}>M<SkeletonBox $w="100%" $h="12px" $radius="6px" /></TextLine></div>
          <div><TextLine style={{ width: '160px' }}>M<SkeletonBox $w="100%" $h="12px" $radius="6px" /></TextLine></div>
        </BoardHead>
      )}
      <RepCard>
        <RepTable ref={tableRef} aria-busy={rows ? undefined : true} data-rep-table="">
          <thead>
            <tr>
              <th>Sales Rep</th>
              {STAGES.map((s) => <th key={s.key}><HeadIn><Dot $stage={s.key} />{stageWithPct(s)}</HeadIn></th>)}
              <th>Total</th>
              <th><HeadIn><DeclinedDot />Declined</HeadIn></th>
            </tr>
          </thead>
          <tbody>
            {rows ? rows.map((r) => (
              <tr key={r.owner}>
                <td>{r.owner}</td>
                {STAGES.map((s) => <td key={s.key}>{amountCell(r.stages[s.key])}</td>)}
                <td><b>{amountCell(r.total)}</b></td>
                <td>{declinedCell(r.declined)}</td>
              </tr>
            )) : REP_SKELETON_ROWS.map((i) => (
              <tr key={i} aria-hidden="true">
                <td>{cellSkeleton('120px')}</td>
                {[0, 1, 2, 3, 4, 5].map((j) => <td key={j}>{cellSkeleton('64px')}</td>)}
              </tr>
            ))}
          </tbody>
          {rows && sum && rows.length > 0 && (
            <tfoot>
              <tr>
                <td>Total</td>
                {STAGES.map((s) => <td key={s.key}>{amountCell(sum.stages[s.key])}</td>)}
                <td>{amountCell(sum.total)}</td>
                <td>{declinedCell(sum.declined)}</td>
              </tr>
            </tfoot>
          )}
        </RepTable>
        {rows && rows.length === 0 && <RepEmpty>No cards in {monthLabel(month)} yet</RepEmpty>}
      </RepCard>
    </>
  );
};

const SalesPipelinePage: React.FC = () => {
  const { enqueue } = useSaveQueue();

  const [board, setBoard] = useState<PipelineBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  // What the dropdowns ASK for; the server answers with what may be shown (board.month / board.salesPerson).
  const [reqMonth, setReqMonth] = useState('');
  const [reqPerson, setReqPerson] = useState('');
  const reqId = useRef(0);
  const hasData = useRef(false);
  const boardState = useRef<PipelineBoard | null>(null);
  boardState.current = board;

  // The add line.
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [amount, setAmount] = useState('');
  const [stage, setStage] = useState<PipelineStage | ''>('');
  const [closeDate, setCloseDate] = useState(''); // [CLOSE-DATE 2026-10-12] YYYY-MM-DD
  const [bad, setBad] = useState<Set<string>>(() => new Set());
  const [formMsg, setFormMsg] = useState('');
  const [saving, setSaving] = useState(false);
  // [CARD-EDIT 2026-10-07] The card whose amount is being changed (one at a time) and the text in its box.
  const [editing, setEditing] = useState<{ id: number; value: string } | null>(null);
  // [CARD-ACTIVITY 2026-10-07] Each card's HubSpot + Google Meet activity, by card id (filled after the board draws).
  const [activity, setActivity] = useState<Record<number, PipelineCardActivity>>({});

  // Card elements by deal id, the board scroller, gliding copies, and the move waiting to animate.
  const cardEls = useRef(new Map<number, HTMLElement>());
  const boardEl = useRef<HTMLDivElement>(null);
  const ghosts = useRef(new Set<HTMLElement>());
  const flip = useRef<FlipPlan | null>(null);

  const load = useCallback(async (silent = false) => {
    const id = ++reqId.current;
    if (!silent) setLoading(true);
    try {
      const data = await fetchSalesPipeline(reqMonth || undefined, reqPerson || undefined);
      if (id !== reqId.current) return;
      hasData.current = true;
      setBoard(data);
      setError(null);
    } catch (e) {
      if (id !== reqId.current) return;
      // A silent refresh that fails keeps the board on screen.
      if (!silent || !hasData.current) setError(e);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [reqMonth, reqPerson]);

  // [REP-WISE 2026-10-13, user] Main Screen | Rep wise data. "Every rep at once": an admin / senior with one rep picked on
  // the Main Screen still gets every rep — that month's board without the rep filter, asked on its own so the Main Screen
  // keeps its pick. Otherwise (All Sales Rep, or a rep's own board) the board already holds exactly those cards.
  // [REP-WISE 2026-10-13, user] "this toggle switch will be with the admins and seniors only": the server's canSelectAll
  // (admin or senior) once the board is in, the signed-in user's own flags for the loading skeleton before that.
  const { user } = useAuth();
  const canSwitch = board ? board.canSelectAll : !!(user?.is_admin || user?.is_senior);
  const [view, setView] = useState<PipelineView>('main');
  const shownView: PipelineView = canSwitch ? view : 'main';
  const needAll = shownView === 'rep' && !!board?.canSelectAll && !!board?.salesPerson;
  const boardMonth = board?.month ?? '';
  const [allReps, setAllReps] = useState<{ month: string; items: PipelineDeal[] } | null>(null);
  const [allFailed, setAllFailed] = useState(false);
  const allReqId = useRef(0);
  const loadAll = useCallback(async () => {
    if (!boardMonth) return;
    const id = ++allReqId.current;
    setAllFailed(false);
    try {
      const data = await fetchSalesPipeline(boardMonth);
      if (id === allReqId.current) setAllReps({ month: data.month, items: data.items });
    } catch {
      if (id === allReqId.current) setAllFailed(true); // shown only while there is no table yet
    }
  }, [boardMonth]);
  useEffect(() => { if (needAll) loadAll(); }, [needAll, loadAll]);
  const repSource = !board ? null
    : needAll ? (allReps && allReps.month === board.month ? allReps.items : null)
    : board.items;
  const repRows = useMemo(() => (repSource ? repRowsOf(repSource) : null), [repSource]);
  const repLandKey = repRows ? `${boardMonth}\u0001${needAll ? 'all' : 'board'}` : null;

  useEffect(() => { load(); }, [load]);
  // Anyone's save or move bumps 'salesPipeline' on the server; open boards refresh quietly.
  // [PAID-GREEN 2026-10-07] …and a payment change, so a card turns green when its New Sale money lands.
  // [REP-WISE 2026-10-13] …and the every-rep table with it while it is on show.
  useRealtime(['salesPipeline', 'payments'], () => { load(true); if (needAll) loadAll(); });

  useLayoutEffect(() => {
    const plan = flip.current;
    if (!plan) return;
    flip.current = null;
    runFlip(plan, cardEls.current, boardEl.current, ghosts.current);
  }, [board]);

  useEffect(() => {
    const live = ghosts.current;
    return () => { live.forEach((g) => g.remove()); live.clear(); };
  }, []);

  const setCardEl = (id: number) => (el: HTMLElement | null) => {
    if (el) cardEls.current.set(id, el);
    else cardEls.current.delete(id);
  };

  /** Land any card still gliding at once — only before ANOTHER move measures the board. */
  const settleFlights = () => ghosts.current.forEach((g) => g.getAnimations().forEach((a) => a.finish()));

  /** Measure every card BEFORE a state change; the layout effect animates from there. */
  const prepareFlip = (moved: number | null, added: number | null = null) => {
    if (reducedMotion()) { flip.current = null; return; }
    const first = new Map<number, DOMRect>();
    cardEls.current.forEach((el, id) => first.set(id, el.getBoundingClientRect()));
    flip.current = { first, moved, added };
  };

  const columns = useMemo(() => {
    const items = board?.items ?? [];
    return STAGES.map((s) => {
      const deals = items.filter((d) => d.stage === s.key).sort(byLatest);
      // [DECLINE 2026-10-07] A declined card's money is not expected any more, so it is left out of the totals.
      return { ...s, deals, total: deals.reduce((sum, d) => sum + (d.declined ? 0 : d.amount), 0) };
    });
  }, [board]);

  // [CARD-ACTIVITY 2026-10-07] Asked once the board has drawn (it never waits on HubSpot), again when the month, the
  // rep or the set of cards changes, and every 5 minutes while the tab is open. A move or edit changes none of these.
  const activityKey = board && board.ready && board.items.length
    ? `${board.month}\u0001${board.salesPerson}\u0001${board.items.map((d) => d.id).sort((a, b) => a - b).join(',')}`
    : '';
  useEffect(() => {
    if (!activityKey) return undefined;
    const [month, person] = activityKey.split('\u0001');
    let alive = true;
    const run = () => {
      fetchPipelineActivity(month, person || undefined)
        .then((r) => { if (alive) setActivity((prev) => ({ ...prev, ...r.items })); })
        .catch(() => { /* the lines keep their placeholder; the next round tries again */ });
    };
    run();
    const timer = window.setInterval(run, 5 * 60_000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [activityKey]);

  const ready = !!board?.ready;
  const closed = !!board && board.month !== board.currentMonth;
  const locked = !board || !ready || closed;
  const total = columns.reduce((sum, c) => sum + c.total, 0);
  const dealCount = columns.reduce((sum, c) => sum + c.deals.length, 0);
  const whoLabel = board ? (board.salesPerson || (board.canSelectAll ? 'All Sales Rep' : board.me)) : '';

  const moveDeal = (deal: PipelineDeal, to: PipelineStage) => {
    if (locked || to === deal.stage) return;
    const from = deal.stage;
    settleFlights();
    prepareFlip(deal.id);
    const now = new Date().toISOString();
    setBoard((b) => (b ? { ...b, items: b.items.map((d) => (d.id === deal.id ? { ...d, stage: to, stageChangedAt: now } : d)) } : b));

    const { promise, ahead } = enqueue(() => movePipelineDeal(deal.id, to));
    if (ahead > 0) queuedToast('Stage change', ahead);
    promise.then((saved) => {
      const shown = boardState.current?.items.find((d) => d.id === saved.id);
      if (shown && shown.stage !== saved.stage) {
        // Someone else moved it a moment earlier: glide to where it really is.
        settleFlights();
        prepareFlip(saved.id);
        setBoard((b) => (b ? { ...b, items: b.items.map((d) => (d.id === saved.id ? saved : d)) } : b));
      } else {
        // The reply usually lands while the card is still gliding: keep the order it is landing in and
        // leave the flight alone (taking the server's time here could reorder the column mid-flight).
        setBoard((b) => (b ? { ...b, items: b.items.map((d) => (d.id === saved.id ? { ...saved, stageChangedAt: d.stageChangedAt } : d)) } : b));
      }
      if (saved.stage === to) toastSuccess(`${saved.website} moved to ${STAGE_LABEL[to]}`);
      else toast(`${saved.website} is in ${STAGE_LABEL[saved.stage]}. Someone changed it just now.`, { kind: 'info' });
    }).catch((e) => {
      const shown = boardState.current?.items.find((d) => d.id === deal.id);
      if (shown && shown.stage === to) {
        settleFlights();
        prepareFlip(deal.id);
        setBoard((b) => (b ? { ...b, items: b.items.map((d) => (d.id === deal.id ? { ...d, stage: from, stageChangedAt: deal.stageChangedAt } : d)) } : b));
      }
      toastError(errorText(e, 'Could not move the card. Please try again.'));
      if (e?.response?.status === 404 || e?.response?.status === 409) load(true);
    });
  };

  /** Put the server's copy of a card on the board, keeping the order it is shown in (its local move time). */
  const replaceItem = (saved: PipelineDeal) =>
    setBoard((b) => (b ? { ...b, items: b.items.map((d) => (d.id === saved.id ? { ...saved, stageChangedAt: d.stageChangedAt } : d)) } : b));
  const patchItem = (id: number, change: Partial<PipelineDeal>) =>
    setBoard((b) => (b ? { ...b, items: b.items.map((d) => (d.id === id ? { ...d, ...change } : d)) } : b));

  // [CARD-EDIT 2026-10-07, user] Change the amount / payment type of a saved card. Shown at once; the "updated"
  // message waits for the server; a refusal puts the old value back.
  const saveEdit = (deal: PipelineDeal, change: { amount?: number; paymentType?: string; closeDate?: string }, label: string) => {
    if (locked) return;
    const before = { amount: deal.amount, paymentType: deal.paymentType, closeDate: deal.closeDate ?? null };
    patchItem(deal.id, change);
    const { promise, ahead } = enqueue(() => editPipelineDeal(deal.id, {
      ...(change.amount !== undefined ? { amount: String(change.amount) } : {}),
      ...(change.paymentType !== undefined ? { paymentType: change.paymentType } : {}),
      ...(change.closeDate !== undefined ? { closeDate: change.closeDate } : {}), // [CLOSE-DATE 2026-10-12]
    }));
    if (ahead > 0) queuedToast(label, ahead);
    promise.then((saved) => {
      replaceItem(saved);
      toastSuccess(`${saved.website}: ${label.toLowerCase()} updated`);
    }).catch((e) => {
      patchItem(deal.id, before);
      toastError(errorText(e, 'Could not save the change. Please try again.'));
      if (e?.response?.status === 404 || e?.response?.status === 409) load(true);
    });
  };
  const saveAmount = (deal: PipelineDeal) => {
    const text = editing && editing.id === deal.id ? editing.value : '';
    const amount = parseAmount(text);
    if (!(amount > 0)) { toastError('Enter an amount above 0.'); return; }
    setEditing(null);
    const rounded = Math.round(amount * 100) / 100;
    if (rounded !== deal.amount) saveEdit(deal, { amount: rounded }, 'Amount');
  };

  // [DECLINE 2026-10-07, user] Mark declined / refused (the card turns red), or undo it.
  const toggleDecline = (deal: PipelineDeal) => {
    if (locked) return;
    const next = !deal.declined;
    patchItem(deal.id, { declined: next });
    const { promise, ahead } = enqueue(() => setPipelineDealDeclined(deal.id, next));
    if (ahead > 0) queuedToast('Decline', ahead);
    promise.then((saved) => {
      replaceItem(saved);
      toastSuccess(saved.declined ? `${saved.website} marked as declined` : `${saved.website} is open again`);
    }).catch((e) => {
      patchItem(deal.id, { declined: !next });
      toastError(errorText(e, 'Could not save the change. Please try again.'));
      if (e?.response?.status === 404 || e?.response?.status === 409) load(true);
    });
  };

  // [SUGGEST 2026-10-07, user] HubSpot suggestions for the two boxes.
  const loadWebsites = async (q: string) => (await fetchPipelineWebsiteSuggestions(q)).map((s) => ({
    key: s.website,
    main: s.website,
    sub: [s.companyName, s.owner ? `Owner: ${s.owner}` : 'No owner in HubSpot'].filter(Boolean).join(' · '),
    tag: s.mine ? 'Yours' : null,
    fill: s.email,
  }));
  const loadEmails = async (q: string) => (await fetchPipelineEmailSuggestions(q, normalizeWebsite(website))).map((s) => ({
    key: s.email,
    main: s.email,
    sub: s.name,
    tag: s.fromWebsite ? 'This website' : null,
  }));
  const siteChosen = SITE_RE.test(normalizeWebsite(website));

  const clearBad = (key: string) => {
    if (!bad.has(key)) return;
    const next = new Set(bad);
    next.delete(key);
    setBad(next);
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!board || locked || saving) return;
    const site = normalizeWebsite(website);
    const amt = parseAmount(amount);
    const wrong = new Set<string>();
    const problems: string[] = [];
    if (!SITE_RE.test(site)) { wrong.add('website'); problems.push(website.trim() ? 'Company URL (like abc.com)' : 'Company URL'); }
    if (!EMAIL_RE.test(email.trim())) { wrong.add('email'); problems.push(email.trim() ? 'Company Email (like name@abc.com)' : 'Company Email'); }
    if (!paymentType) { wrong.add('paymentType'); problems.push('Payment Type'); }
    // [OWNER-FOLLOWS-REP 2026-10-07, user] Admin/senior: the Deal Owner IS the Sales Rep picked at the top right.
    if (board.canSelectAll && !board.salesPerson) { wrong.add('owner'); problems.push('Deal Owner (pick a Sales Rep at the top right)'); }
    if (!(amt > 0)) { wrong.add('amount'); problems.push(amount.trim() ? 'Amount (more than 0)' : 'Amount'); }
    if (!stage) { wrong.add('stage'); problems.push('Deal Stage'); }
    if (!closeDate) { wrong.add('closeDate'); problems.push('Tentative Close Date'); } // [CLOSE-DATE 2026-10-12]
    setBad(wrong);
    if (problems.length) {
      setFormMsg(`All boxes are needed to save. Please fill: ${problems.join(', ')}.`);
      return;
    }
    setFormMsg('');
    setSaving(true);
    try {
      const saved = await addPipelineDeal({
        month: board.month, website: site, email: email.trim(), paymentType, amount, stage: stage as PipelineStage,
        closeDate, // [CLOSE-DATE 2026-10-12]
        ...(board.canSelectAll ? { owner: board.salesPerson } : {}),
      });
      const shows = !board.salesPerson || board.salesPerson === saved.owner;
      if (shows) {
        prepareFlip(null, saved.id);
        setBoard((b) => (b ? { ...b, items: [saved, ...b.items.filter((d) => d.id !== saved.id)] } : b));
      }
      toastSuccess(shows
        ? `Saved. ${saved.website} is in ${STAGE_LABEL[saved.stage]}.`
        : `Saved for ${saved.owner}. Pick ${saved.owner} or All Sales Rep to see it.`);
      setWebsite(''); setEmail(''); setPaymentType(''); setAmount(''); setStage(''); setCloseDate('');
      setBad(new Set());
    } catch (err: any) {
      const dup = err?.response?.status === 409 ? err?.response?.data?.duplicate : null;
      if (dup) {
        // [DUP-POPUP 2026-10-07, user] The website is already on this month's board (any section, anyone's board):
        // a centred popup with OK says which section and which rep it is under. Nothing is saved.
        setBad(new Set(['website']));
        setFormMsg('');
        alertDialog({
          title: 'Website already on the board',
          icon: 'fa-circle-info',
          message: (
            <>
              <b>{dup.website}</b> is already on the {monthLabel(String(dup.month))} board.
              <br />
              Section: <b>{STAGE_LABEL[dup.stage as PipelineStage] ?? dup.stage}</b> · Deal Owner: <b>{dup.owner}</b>
            </>
          ),
        });
        return;
      }
      const msg = errorText(err, 'Could not save. Please try again.');
      setFormMsg(msg);
      toastError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!board) {
    if (error && isForbidden(error)) {
      return <Page><Plain>You don't have access to the Sales Pipeline. Ask an admin to tick it for you in User Management.</Plain></Page>;
    }
    return (
      <Page>
        <Toolbar>
          <Title>Sales Pipeline</Title>
          {/* [LOADING-FORM 2026-10-07, user] "why the skeleton is not showing for this": the Month / Sales Rep dropdowns
              and the whole add line now have skeletons too, in the real sizes, so nothing moves when the data lands. */}
          {!error && (
            <Filters data-skeleton="filters">
              <FilterSkeleton aria-hidden="true" />
              <FilterSkeleton aria-hidden="true" />
              {/* [REP-WISE 2026-10-13] The switch needs no data, so it is there from the start (admins / seniors). */}
              {canSwitch && <ViewPill view={shownView} onPick={setView} />}
            </Filters>
          )}
        </Toolbar>
        {error ? (
          <LoadFailed what="the sales pipeline" onRetry={() => load()} />
        ) : (
          <>
            <Entry as="div" aria-hidden="true" data-skeleton="entry">
              {ENTRY_SKELETON.map((f) => (
                <Field key={f.key} $wide={f.wide}>
                  <Label as="div"><TextLine style={{ width: f.label }}>M<SkeletonBox $w="100%" $h="10px" $radius="5px" /></TextLine></Label>
                  <SkeletonBox $h="36px" $radius="8px" />
                </Field>
              ))}
              <Field $save>
                <Label as="span">&nbsp;</Label>
                <SaveSkeleton />
              </Field>
            </Entry>
            <BoardHead aria-hidden="true">
              <div><TextLine style={{ width: '220px' }}>M<SkeletonBox $w="100%" $h="12px" $radius="6px" /></TextLine></div>
              <div><TextLine style={{ width: '160px' }}>M<SkeletonBox $w="100%" $h="12px" $radius="6px" /></TextLine></div>
            </BoardHead>
            <Board aria-busy="true">
              {STAGES.map((s) => (
                <Column key={s.key}>
                  <ColumnHead><Dot $stage={s.key} /><ColumnName>{stageWithPct(s)}</ColumnName><Count style={{ visibility: 'hidden' }}>0</Count></ColumnHead>
                  <ColumnBody>
                    <SkeletonBox $h="118px" $radius="10px" />
                    <SkeletonBox $h="118px" $radius="10px" />
                  </ColumnBody>
                </Column>
              ))}
            </Board>
          </>
        )}
      </Page>
    );
  }

  const personOptions = board.salesPerson && !board.salesPeople.includes(board.salesPerson)
    ? [...board.salesPeople, board.salesPerson].sort((a, b) => a.localeCompare(b))
    : board.salesPeople;

  return (
    <Page>
      <Toolbar>
        <Title>Sales Pipeline</Title>
        <Filters $solo={shownView === 'rep'}>
          {/* [REP-WISE 2026-10-13, user] "month will be right and all sales rep will be left": Sales Rep, Month, switch. */}
          <RepPick
            liquid
            $off={shownView === 'rep'}
            value={board.salesPerson}
            onChange={(e) => { setReqPerson(e.target.value); clearBad('owner'); }}
            disabled={shownView === 'rep' || (!board.canSelectAll && personOptions.length <= 1)}
            title="Sales Rep"
          >
            {board.canSelectAll && <option value="">All Sales Rep</option>}
            {personOptions.map((n) => <option key={n} value={n}>{n}</option>)}
          </RepPick>
          <GlassSelect liquid value={board.month} onChange={(e) => setReqMonth(e.target.value)} title="Month">
            {board.months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </GlassSelect>
          {canSwitch && <ViewPill view={shownView} onPick={setView} />}
        </Filters>
      </Toolbar>

      {/* [REP-WISE 2026-10-13, user] Only what is BELOW the toolbar slides (components/ViewSlide); $quiet: the switch
          itself never replays a data animation (feedback "toggles quiet + instant"). Both views stay mounted. */}
      <PaneViewport>
        <SlidePane $pos={slidePos(shownView, 'main', VIEWS)} $quiet aria-hidden={shownView !== 'main'} data-pipeline-pane="main">
          <Entry onSubmit={onSave} noValidate>
            <Field $wide>
              <Label htmlFor="sp-website">Company URL</Label>
              {/* [SUGGEST 2026-10-07, user] HubSpot companies while typing; picking one also fills an empty email box. */}
              <SuggestBox
                id="sp-website" value={website} placeholder="abc.com" disabled={locked} bad={bad.has('website')} minChars={2}
                onChange={(v) => { setWebsite(v); clearBad('website'); }}
                load={loadWebsites}
                onPick={(it) => {
                  setWebsite(it.main);
                  clearBad('website');
                  if (!email.trim() && it.fill) { setEmail(it.fill); clearBad('email'); }
                }}
              />
            </Field>
            <Field $wide>
              <Label htmlFor="sp-email">Company Email</Label>
              {/* [SUGGEST 2026-10-07, user] The chosen website's HubSpot emails first, then any HubSpot email. */}
              <SuggestBox
                id="sp-email" type="email" value={email} placeholder="name@abc.com" disabled={locked} bad={bad.has('email')}
                minChars={2} showWhenEmpty={siteChosen}
                onChange={(v) => { setEmail(v); clearBad('email'); }}
                load={loadEmails}
                onPick={(it) => { setEmail(it.main); clearBad('email'); }}
              />
            </Field>
            <Field>
              <Label as="span">Payment Type</Label>
              <BoxSelect liquidList
                value={paymentType} disabled={locked} $bad={bad.has('paymentType')} title="Payment Type"
                onChange={(e) => { setPaymentType(e.target.value); clearBad('paymentType'); }}
              >
                <option value="">Select</option>
                {PAYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </BoxSelect>
            </Field>
            <Field>
              <Label as="span">Deal Owner</Label>
              {/* [OWNER-FOLLOWS-REP 2026-10-07, user] No dropdown: a rep's own name, or (admin/senior) the Sales Rep
                  picked at the top right. */}
              <Locked
                data-owner-box=""
                $bad={bad.has('owner')}
                $empty={board.canSelectAll && !board.salesPerson}
                title={board.canSelectAll ? 'Deal Owner is the Sales Rep picked at the top right' : 'Deal Owner is you'}
              >
                <i className="fas fa-lock" aria-hidden="true" />
                <span>{board.canSelectAll ? (board.salesPerson || 'Pick a Sales Rep above') : board.me}</span>
              </Locked>
            </Field>
            <Field>
              <Label htmlFor="sp-amount">Amount</Label>
              <Money>
                <span aria-hidden="true">$</span>
                <Input
                  id="sp-amount" value={amount} placeholder="0" inputMode="decimal" autoComplete="off"
                  disabled={locked} $bad={bad.has('amount')}
                  onChange={(e) => { setAmount(e.target.value); clearBad('amount'); }}
                />
              </Money>
            </Field>
            <Field>
              <Label as="span">Deal Stage</Label>
              <BoxSelect liquidList
                value={stage} disabled={locked} $bad={bad.has('stage')} title="Deal Stage"
                onChange={(e) => { setStage(e.target.value as PipelineStage | ''); clearBad('stage'); }}
              >
                <option value="">Select</option>
                {STAGES.map((s) => <option key={s.key} value={s.key}>{stageWithPct(s)}</option>)}
              </BoxSelect>
            </Field>
            <Field $wide>
              <Label as="span">Tentative Close Date</Label>
              {/* [CLOSE-DATE 2026-10-12, user] "it will have the calender thing to select the date": today or later. */}
              <DateBox $bad={bad.has('closeDate')} $off={locked} data-close-date-box="">
                <SingleDateCalendar liquid value={closeDate} min={todayKey()} onChange={(d) => { setCloseDate(d); clearBad('closeDate'); }} />
              </DateBox>
            </Field>
            <Field $save>
              <Label as="span" aria-hidden="true">&nbsp;</Label>
              <SaveButton type="submit" disabled={locked || saving}>{saving ? 'Saving…' : 'Save'}</SaveButton>
            </Field>
          </Entry>
          {formMsg && <FormMsg role="alert">{formMsg}</FormMsg>}
          {!ready && (
            <Notice><i className="fas fa-circle-info" aria-hidden="true" />The Sales Pipeline switches on with the next update of the dashboard.</Notice>
          )}
          {ready && closed && (
            <Notice>
              <i className="fas fa-lock" aria-hidden="true" />
              {monthLabel(board.month)} is closed: you can look at it, not change it. New deals go into {monthLabel(board.currentMonth)}.
            </Notice>
          )}

          <BoardHead>
            {/* [CLIENTS-LABEL 2026-10-12, user] "October 2026 · 64 Clients · All Sales Rep" — "change deals to clients". */}
            <div><b>{monthLabel(board.month)}</b> · {dealCount} {dealCount === 1 ? 'Client' : 'Clients'} · {whoLabel}</div>
            <div>Pipeline total <b>{money(total)}</b></div>
          </BoardHead>
          <Board ref={boardEl} $dim={loading}>
            {columns.map((col) => (
              <Column key={col.key} data-col={col.key} aria-label={col.label}>
                <ColumnHead>
                  <Dot $stage={col.key} />
                  <ColumnName>{stageWithPct(col)}</ColumnName>
                  <Count>{col.deals.length}</Count>
                </ColumnHead>
                <ColumnBody data-col-body="">
                  {col.deals.length === 0 && <Empty>No deals in {col.label} yet</Empty>}
                  {col.deals.map((d) => {
                    // [PAID-GREEN 2026-10-07, user] Green once this month's New Sale money for the website reaches the
                    // amount; [DECLINE 2026-10-07, user] red when marked declined.
                    const paid = d.paidAmount != null && d.paidAmount >= d.amount;
                    const tone: CardTone = paid ? 'paid' : d.declined ? 'declined' : null;
                    // [NO-CARD-TIP 2026-10-13, user] "i dont want to see global tooltip, i want my own tooltip only": the
                    // card itself has no hover text any more (was "Declined: this money is not expected" / "New Sale
                    // received this month: $X"); only the controls on it keep theirs, e.g. "Declined. Click to undo".
                    return (
                    <Card key={d.id} ref={setCardEl(d.id)} $tone={tone} data-tone={tone ?? ''}>
                      <CardTop>
                        <Site>{d.website}</Site>
                        {locked ? (
                          <TypePill $recurring={d.paymentType === 'Recurring'}>{d.paymentType}</TypePill>
                        ) : (
                          <TypeSelect liquidList
                            value={d.paymentType}
                            $recurring={d.paymentType === 'Recurring'}
                            title="Change Recurring / One-Time"
                            onChange={(e) => { if (e.target.value !== d.paymentType) saveEdit(d, { paymentType: e.target.value }, 'Payment type'); }}
                          >
                            {PAYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </TypeSelect>
                        )}
                      </CardTop>
                      {editing && editing.id === d.id ? (
                        <AmountEdit onSubmit={(e) => { e.preventDefault(); saveAmount(d); }}>
                          <span aria-hidden="true">$</span>
                          <input
                            autoFocus
                            value={editing.value}
                            inputMode="decimal"
                            aria-label={`New amount for ${d.website}`}
                            onChange={(e) => setEditing({ id: d.id, value: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }}
                          />
                          <IconButton type="submit" title="Save the amount"><i className="fas fa-check" aria-hidden="true" /></IconButton>
                          <IconButton type="button" title="Cancel" onClick={() => setEditing(null)}><i className="fas fa-xmark" aria-hidden="true" /></IconButton>
                        </AmountEdit>
                      ) : locked ? (
                        <Amount>{money(d.amount)}</Amount>
                      ) : (
                        <AmountButton type="button" title="Change the amount" onClick={() => setEditing({ id: d.id, value: String(d.amount) })}>
                          {money(d.amount)}
                          <i className="fas fa-pen" aria-hidden="true" />
                        </AmountButton>
                      )}
                      <Line>{d.email}</Line>
                      {/* [CARD-LINES 2026-10-07, user] website / amount / email / Deal Owner / Added — one per line. */}
                      <Line $small>Deal Owner: {d.owner}</Line>
                      {/* [CLOSE-DATE 2026-10-12, user] Under Deal Owner; on an open month the date opens the calendar. */}
                      {locked ? (
                        <Line $small data-close-date="">Tentative Close Date: {closeDateText(d.closeDate)}</Line>
                      ) : (
                        <CardDate data-close-date="">
                          <span>Tentative Close Date:</span>
                          <SingleDateCalendar
                            value={d.closeDate ?? ''}
                            min={todayKey()}
                            onChange={(v) => { if (v !== d.closeDate) saveEdit(d, { closeDate: v }, 'Tentative close date'); }}
                          />
                        </CardDate>
                      )}
                      <Line $small>Added {dayLabel(d.addedAt)}</Line>
                      <CardActivityLines a={activity[d.id]} inHubSpot={!!d.hubspotCompanyId} month={d.month} />
                      <CardActions>
                        <ActionsLeft>
                          <StageSelect liquidList
                            value={d.stage}
                            disabled={locked}
                            title={locked ? 'This month is closed' : 'Move to another stage'}
                            onChange={(e) => moveDeal(d, e.target.value as PipelineStage)}
                          >
                            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                          </StageSelect>
                          {(!locked || d.declined) && (
                            <DeclineButton
                              type="button"
                              $on={d.declined}
                              disabled={locked}
                              aria-pressed={d.declined}
                              title={d.declined ? 'Declined. Click to undo' : 'Mark as declined / refused'}
                              onClick={() => toggleDecline(d)}
                            >
                              <i className={d.declined ? 'fas fa-ban' : 'fas fa-xmark'} aria-hidden="true" />
                              {d.declined ? 'Declined' : 'Decline'}
                            </DeclineButton>
                          )}
                        </ActionsLeft>
                        {d.hubspotCompanyId ? (
                          <HubSpotLink href={hubspotCompanyUrl(d.hubspotCompanyId)} target="_blank" rel="noopener noreferrer">
                            View on HubSpot <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
                          </HubSpotLink>
                        ) : (
                          <NotInHubSpot title="No HubSpot company has this website">Not in HubSpot</NotInHubSpot>
                        )}
                      </CardActions>
                    </Card>
                    );
                  })}
                </ColumnBody>
                <ColumnFoot><b>{money(col.total)}</b> | Total amount</ColumnFoot>
              </Column>
            ))}
          </Board>
        </SlidePane>
        {canSwitch && (
          <SlidePane $pos={slidePos(shownView, 'rep', VIEWS)} $quiet aria-hidden={shownView !== 'rep'} data-pipeline-pane="rep">
            <RepWiseTable month={board.month} rows={repRows} failed={needAll && allFailed} onRetry={loadAll} landKey={repLandKey} />
          </SlidePane>
        )}
      </PaneViewport>
    </Page>
  );
};

export default SalesPipelinePage;
